import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const SkillOntologyPanel = ({ onUpdateJobDescriptions }) => {
  const { t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedJobLevel, setSelectedJobLevel] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);

  const languages = [
    { value: 'en', label: 'English', flag: '🇬🇧' },
    { value: 'de', label: 'German (Deutsch)', flag: '🇩🇪' },
    { value: 'es', label: 'Spanish (Español)', flag: '🇪🇸' },
    { value: 'pl', label: 'Polish (Polski)', flag: '🇵🇱' },
    { value: 'nl', label: 'Dutch (Nederlands)', flag: '🇳🇱' }
  ];

  const jobLevels = [
    { value: 'entry', label: 'Entry Level (0-2 years)' },
    { value: 'mid', label: 'Mid Level (3-5 years)' },
    { value: 'senior', label: 'Senior Level (6-8 years)' },
    { value: 'lead', label: 'Lead Level (9-12 years)' },
    { value: 'executive', label: 'Executive Level (12+ years)' }
  ];

  const departments = [
    { value: 'engineering', label: 'Engineering & Technology' },
    { value: 'product', label: 'Product Management' },
    { value: 'design', label: 'Design & UX' },
    { value: 'marketing', label: 'Marketing & Communications' },
    { value: 'sales', label: 'Sales & Business Development' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'finance', label: 'Finance & Accounting' },
    { value: 'operations', label: 'Operations & Supply Chain' },
    { value: 'legal', label: 'Legal & Compliance' },
    { value: 'data', label: 'Data Science & Analytics' }
  ];

  // Comprehensive skill ontology by department
  const skillOntology = {
    engineering: {
      technical: [
        'JavaScript', 'TypeScript', 'React', 'Node.js', 'Python', 'Java', 'C++', 'Go',
        'Docker', 'Kubernetes', 'AWS', 'Azure', 'GCP', 'PostgreSQL', 'MongoDB',
        'Redis', 'Elasticsearch', 'GraphQL', 'REST API', 'Microservices'
      ],
      soft: [
        'Problem Solving', 'Code Review', 'System Design', 'Technical Leadership',
        'Mentoring', 'Agile Methodologies', 'Cross-functional Collaboration'
      ]
    },
    product: {
      technical: [
        'Product Strategy', 'Roadmap Planning', 'User Research', 'A/B Testing',
        'Analytics', 'Wireframing', 'Prototyping', 'Jira', 'Confluence', 'Figma'
      ],
      soft: [
        'Strategic Thinking', 'Customer Advocacy', 'Stakeholder Management',
        'Data-driven Decision Making', 'Market Analysis', 'Competitive Intelligence'
      ]
    },
    design: {
      technical: [
        'Figma', 'Sketch', 'Adobe Creative Suite', 'Principle', 'InVision',
        'UI Design', 'UX Research', 'Prototyping', 'Design Systems', 'Accessibility'
      ],
      soft: [
        'Creative Thinking', 'User Empathy', 'Visual Communication',
        'Design Critique', 'Iteration', 'Cross-team Collaboration'
      ]
    },
    marketing: {
      technical: [
        'Google Analytics', 'HubSpot', 'Salesforce', 'Content Management Systems',
        'SEO/SEM', 'Social Media Marketing', 'Email Marketing', 'Marketing Automation'
      ],
      soft: [
        'Brand Strategy', 'Content Creation', 'Campaign Management',
        'Market Research', 'Customer Segmentation', 'Creative Strategy'
      ]
    },
    sales: {
      technical: [
        'CRM Systems', 'Salesforce', 'Pipeline Management', 'Lead Generation Tools',
        'Sales Analytics', 'Proposal Writing', 'Contract Negotiation'
      ],
      soft: [
        'Relationship Building', 'Negotiation', 'Presentation Skills',
        'Active Listening', 'Objection Handling', 'Territory Management'
      ]
    },
    hr: {
      technical: [
        'HRIS Systems', 'Talent Acquisition', 'Performance Management',
        'Compensation Analysis', 'Employee Relations', 'Training & Development'
      ],
      soft: [
        'Interpersonal Skills', 'Conflict Resolution', 'Change Management',
        'Cultural Competency', 'Coaching', 'Strategic HR Planning'
      ]
    },
    finance: {
      technical: [
        'Financial Modeling', 'Excel/Google Sheets', 'SAP', 'QuickBooks',
        'Financial Reporting', 'Budgeting & Forecasting', 'Risk Analysis'
      ],
      soft: [
        'Analytical Thinking', 'Attention to Detail', 'Business Acumen',
        'Strategic Planning', 'Stakeholder Communication', 'Process Improvement'
      ]
    },
    operations: {
      technical: [
        'Supply Chain Management', 'Process Optimization', 'ERP Systems',
        'Quality Management', 'Inventory Management', 'Vendor Relations'
      ],
      soft: [
        'Project Management', 'Process Improvement', 'Cross-functional Leadership',
        'Problem Solving', 'Resource Planning', 'Change Management'
      ]
    },
    legal: {
      technical: [
        'Contract Law', 'Regulatory Compliance', 'Intellectual Property',
        'Employment Law', 'Data Privacy', 'Legal Research', 'Litigation Management'
      ],
      soft: [
        'Legal Writing', 'Negotiation', 'Risk Assessment',
        'Client Advisory', 'Regulatory Interpretation', 'Cross-team Collaboration'
      ]
    },
    data: {
      technical: [
        'Python', 'R', 'SQL', 'Tableau', 'Power BI', 'Machine Learning',
        'Statistical Analysis', 'Data Modeling', 'ETL Processes', 'Big Data Technologies'
      ],
      soft: [
        'Analytical Thinking', 'Data Storytelling', 'Business Intelligence',
        'Statistical Interpretation', 'Cross-functional Communication', 'Problem Solving'
      ]
    }
  };

  // Get skills based on selected department
  const getAvailableSkills = () => {
    if (!selectedDepartment) return [];
    const departmentSkills = skillOntology?.[selectedDepartment];
    if (!departmentSkills) return [];
    
    const allSkills = [...departmentSkills?.technical, ...departmentSkills?.soft];
    
    if (searchTerm) {
      return allSkills?.filter(skill => 
        skill?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }
    
    return allSkills;
  };

  const handleSkillToggle = (skill) => {
    setSelectedSkills(prev => {
      if (prev?.includes(skill)) {
        return prev?.filter(s => s !== skill);
      } else {
        return [...prev, skill];
      }
    });
  };

  const handleGenerateJobDescriptions = async () => {
    setIsGenerating(true);
    
    // Simulate job description generation
    setTimeout(() => {
      const generatedData = {
        language: selectedLanguage,
        jobLevel: selectedJobLevel,
        department: selectedDepartment,
        skills: selectedSkills,
        timestamp: new Date()?.toISOString(),
        jobDescriptions: generateMockJobDescriptions()
      };
      
      onUpdateJobDescriptions?.(generatedData);
      setIsGenerating(false);
    }, 2000);
  };

  const generateMockJobDescriptions = () => {
    const languageNames = {
      en: 'English',
      de: 'German',
      es: 'Spanish',
      pl: 'Polish',
      nl: 'Dutch'
    };

    return [
      {
        id: 'job-001',
        title: `Senior ${selectedDepartment?.charAt(0)?.toUpperCase() + selectedDepartment?.slice(1)} Professional`,
        language: languageNames?.[selectedLanguage],
        languageCode: selectedLanguage,
        department: selectedDepartment,
        level: selectedJobLevel,
        skills: selectedSkills,
        createdAt: new Date()?.toISOString(),
        payBand: `€${Math.floor(Math.random() * (120000 - 45000) + 45000)?.toLocaleString()}-€${Math.floor(Math.random() * (150000 - 65000) + 65000)?.toLocaleString()}`,
        requirements: selectedSkills?.slice(0, 8),
        preferred: selectedSkills?.slice(8, 12)
      },
      {
        id: 'job-002',
        title: `${selectedJobLevel?.charAt(0)?.toUpperCase() + selectedJobLevel?.slice(1)} ${selectedDepartment?.charAt(0)?.toUpperCase() + selectedDepartment?.slice(1)} Specialist`,
        language: languageNames?.[selectedLanguage],
        languageCode: selectedLanguage,
        department: selectedDepartment,
        level: selectedJobLevel,
        skills: selectedSkills,
        createdAt: new Date()?.toISOString(),
        payBand: `€${Math.floor(Math.random() * (100000 - 35000) + 35000)?.toLocaleString()}-€${Math.floor(Math.random() * (130000 - 55000) + 55000)?.toLocaleString()}`,
        requirements: selectedSkills?.slice(0, 6),
        preferred: selectedSkills?.slice(6, 10)
      }
    ];
  };

  const isConfigurationComplete = selectedLanguage && selectedJobLevel && selectedDepartment && selectedSkills?.length > 0;
  const availableSkills = getAvailableSkills();

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Brain" size={20} className="text-accent" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t('complianceReportsPage.skills2.sectionTitle')}</h2>
            <p className="text-sm text-muted-foreground">{t('complianceReportsPage.skills2.sectionSubtitle')}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Globe" size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Multi-language Support</span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <Select
            label={t('complianceReportsPage.skills.reportLanguage')}
            description="Select the language for job description generation"
            options={languages?.map(lang => ({
              ...lang,
              label: `${lang?.flag} ${lang?.label}`
            }))}
            value={selectedLanguage}
            onChange={setSelectedLanguage}
            placeholder={t('complianceReportsPage.skills.chooseLanguage')}
            required
          />

          <Select
            label={t('complianceReportsPage.skills.jobLevel')}
            description="Define the experience level for role requirements"
            options={jobLevels}
            value={selectedJobLevel}
            onChange={setSelectedJobLevel}
            placeholder={t('complianceReportsPage.skills.selectLevel')}
            required
          />

          <Select
            label="Department"
            description="Choose the department to load relevant skill ontology"
            options={departments}
            value={selectedDepartment}
            onChange={setSelectedDepartment}
            placeholder="Select department"
            searchable
            required
          />
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <Input
            label={t('complianceReportsPage.skills.searchSkills')}
            description="Search within the skill ontology"
            placeholder="Type to search skills..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            iconName="Search"
            disabled={!selectedDepartment}
          />

          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">
              Skills Selection ({selectedSkills?.length} selected)
            </label>
            <div className="max-h-64 overflow-y-auto p-4 bg-muted/50 rounded-md border">
              {!selectedDepartment ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Please select a department to view available skills
                </p>
              ) : availableSkills?.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No skills found matching "{searchTerm}"
                </p>
              ) : (
                <div className="space-y-2">
                  {availableSkills?.map((skill) => (
                    <Checkbox
                      key={skill}
                      label={skill}
                      checked={selectedSkills?.includes(skill)}
                      onChange={() => handleSkillToggle(skill)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Selected Skills Preview */}
      {selectedSkills?.length > 0 && (
        <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-md">
          <h4 className="font-medium text-foreground mb-3">{t('complianceReportsPage.skills2.selectedSkillsPreview')}</h4>
          <div className="flex flex-wrap gap-2">
            {selectedSkills?.map((skill) => (
              <span
                key={skill}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary text-primary-foreground"
              >
                {skill}
                <button
                  onClick={() => handleSkillToggle(skill)}
                  className="ml-2 hover:bg-primary-foreground/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Configuration Summary */}
      {isConfigurationComplete && (
        <div className="mt-6 p-4 bg-success/10 border border-success/20 rounded-md">
          <div className="flex items-start space-x-3">
            <Icon name="CheckCircle" size={20} className="text-success mt-0.5" />
            <div>
              <h4 className="font-medium text-success">{t('complianceReportsPage.skills2.configComplete')}</h4>
              <p className="text-sm text-success/80 mt-1">
                Ready to generate {languages?.find(l => l?.value === selectedLanguage)?.label} job descriptions for {departments?.find(d => d?.value === selectedDepartment)?.label} with {selectedSkills?.length} skills
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Generate Button */}
      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Globe" size={16} />
          <span>Multi-language • Skill Taxonomy • EU Compliant</span>
        </div>
        <Button
          variant="default"
          iconName="Brain"
          iconPosition="left"
          loading={isGenerating}
          disabled={!isConfigurationComplete || isGenerating}
          onClick={handleGenerateJobDescriptions}
        >
          {isGenerating ? 'Generating Descriptions...' : 'Generate Job Descriptions'}
        </Button>
      </div>
    </div>
  );
};

export default SkillOntologyPanel;